import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Creators as ReportActions } from "../../store/ducks/report";
import { Row, Col, Icon, notification, Modal, Tabs } from "antd";
import SlaughterSchedule from "../slaughterSchedule";

// Components
import ButtonStandard from "../../components/utils/button";
import BoitelContractsTable from "./table";
import ContractDetailsDrawer from "./components/ContractDetailsDrawer";
import { CardCustom, Title } from "./styles";
import { transformCamelCaseToSnakeCase } from "../../utils/stringUtils";
import moment from "moment";
import {
  indexBoitelContract,
  showBoitelContract,
  deleteBoitelContract,
} from "../../services/boitelContractService";
import { logEventInfo } from "../../utils/logger";

const TABLE_CHANGE = { page: 0, size: 10, sorter: null, filters: "" };
const TABLE_DATA_PAYLOAD = {
  totalElements: 0,
  size: 0,
  number: 0,
  content: [],
};

const BoitelContracts = () => {
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const history = useHistory();

  const [loadingTable, setLoadingTable] = useState(true);
  const [dataTable, setDataTable] = useState(TABLE_DATA_PAYLOAD);
  const [tableChange, setTableChange] = useState(TABLE_CHANGE);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const getSearchString = useCallback((filters) => {
    let search = "";
    logEventInfo("BoitelContracts - getSearchString - filters", filters);
    if (filters && Object.entries(filters).length > 0) {
      Object.entries(filters).forEach(([field, value]) => {
        if (value && value.length > 0) {
          const fieldS = transformCamelCaseToSnakeCase(field);
          if (
            [
              "amount_animals",
              "entry_weight",
              "contract_days",
              "consecutive_days",
            ].includes(fieldS)
          ) {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                  ? ">"
                  : value[1] === "greaterOrEqualThan"
                    ? ">="
                    : value[1] === "lessThan"
                      ? "<"
                      : value[1] === "lessOrEqualThan"
                        ? "<="
                        : value[1] === "between"
                          ? "between"
                          : "=";
            search =
              search === ""
                ? `${fieldS} ${
                    operatorS === "between"
                      ? `${operatorS} ${value[0]} and ${value[2]}`
                      : `${operatorS} ${value[0]}`
                  }`
                : `${search};${fieldS} ${
                    operatorS === "between"
                      ? `${operatorS} ${value[0]} and ${value[2]}`
                      : `${operatorS} ${value[0]}`
                  }`;
          } else if (
            ["contract_date", "final_date_contract"].includes(fieldS)
          ) {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                  ? ">"
                  : value[1] === "greaterOrEqualThan"
                    ? ">="
                    : value[1] === "lessThan"
                      ? "<"
                      : value[1] === "lessOrEqualThan"
                        ? "<="
                        : "=";

            search =
              search === ""
                ? `cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0],
                  ).format("YYYY-MM-DD")}' as date)`
                : `${search};cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0],
                  ).format("YYYY-MM-DD")}' as date)`;
          } else if (["animal_gender", "status"].includes(fieldS)) {
            search =
              search === ""
                ? `${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`
                : `${search};${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`;
          } else {
            search =
              search === ""
                ? `upper(${fieldS}) like upper('%25${value}%25')`
                : `${search};upper(${fieldS}) like upper('%25${value}%25')`;
          }
        }
      });
    }
    return search !== "" ? search : "";
  }, []);

  const getSortString = useCallback((sorter) => {
    let customSort = sorter;

    if (sorter && Object.entries(sorter).length > 0) {
      const { field } = sorter;

      const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;

      customSort = {
        ...customSort,
        field: fieldS,
      };
    }

    return customSort;
  }, []);

  const fetchData = useCallback(
    async (page = 0, sorter = {}, filters = "", size = 10) => {
      setLoadingTable(true);
      try {
        const {
          data: { results: response },
        } = await indexBoitelContract({
          groupId,
          farmId,
          page,
          size,
          sorter,
          filters,
        });
        setDataTable(response);
      } catch (error) {
        notification.error({
          message: "Erro ao buscar contratos de boitel",
        });
      } finally {
        setLoadingTable(false);
      }
    },
    [groupId, farmId],
  );

  const handleReloadTable = () => {
    const { page, size, sorter, filters } = tableChange;
    fetchData(page, sorter, filters, size);
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    let search = getSearchString(filters);
    let sort = getSortString(sorter);

    logEventInfo("BoitelContracts - handleTableChange - sort", sort);
    logEventInfo("BoitelContracts - handleTableChange - search", search);

    setTableChange({
      page: pagination.current,
      size: pagination.pageSize,
      sorter: sort,
      filters: search,
    });

    fetchData(pagination.current, sort, search, pagination.pageSize);
  };

  const goToNewBoitelContract = () => {
    history.push("/admin/boitel/contracts/new");
  };

  const handleOpenDetailsDrawer = async (record) => {
    setDetailsDrawerVisible(true);
    setSelectedContract(record);
    setDetailsLoading(true);
    try {
      const {
        data: { results },
      } = await showBoitelContract({ groupId, farmId, id: record.id });
      record.herds = results?.herds || [];
      if (results?.herds && record.herds.length > 0) {
        record.herds.forEach((herd) => {
          herd.farmName = herd.cattleProducerPropertyName || null;
        });
      }
      setSelectedContract(record);
    } catch (err) {
      notification.error({
        message: translation?.boitelContractsPage?.form?.errorLoading,
      });
      setDetailsDrawerVisible(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetailsDrawer = () => {
    setDetailsDrawerVisible(false);
    setSelectedContract(null);
  };

  const handleDeleteContract = (record) => {
    const t = translation?.boitelContractsPage?.deleteModal;
    Modal.confirm({
      title: t?.title,
      content: t?.content,
      okText: translation?.buttons?.confirm,
      cancelText: translation?.buttons?.cancel,
      onOk: async () => {
        try {
          await deleteBoitelContract({ groupId, farmId, id: record.id });
          notification.success({ message: t?.success });
          handleReloadTable();
        } catch (err) {
          notification.error({ message: t?.error });
        }
      },
    });
  };

  useEffect(() => {
    fetchData();
  }, [farmId, fetchData, groupId]);

  return (
    <CardCustom>
      {/* Tabs */}
      <Tabs defaultActiveKey="contracts" type="card">
        <Tabs.TabPane
          key="contracts"
          tab={translation.boitelContractsPage?.tabs?.contracts}
        >
          {/* Header */}
          <Row type="flex" justify="space-between" className="rowHeader">
            <Col></Col>
            <Col>
              <Row type="flex" gutter={8}>
                <Col>
                  <ButtonStandard
                    buttonType="typeWithoutBackground"
                    onClick={handleReloadTable}
                  >
                    <Icon type="reload" />
                  </ButtonStandard>
                </Col>
                <Col>
                  <ButtonStandard
                    buttonType="alternative"
                    onClick={() =>
                      dispatch(ReportActions.showModal("ContractsReport"))
                    }
                  >
                    {
                      translation.boitelContractsPage
                        .buttonExportContractsReport
                    }
                  </ButtonStandard>
                </Col>
                <Col>
                  <ButtonStandard
                    buttonType="alternative"
                    onClick={() =>
                      dispatch(
                        ReportActions.showModal("ContractsSaleResultReport")
                      )
                    }
                  >
                    {
                      translation.boitelContractsPage
                        .buttonExportContractsSaleResultReport
                    }
                  </ButtonStandard>
                </Col>
                <Col>
                  <ButtonStandard
                    buttonType="addNew"
                    onClick={goToNewBoitelContract}
                  >
                    {translation.boitelContractsPage.buttonNewContract}
                  </ButtonStandard>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row type="flex" justify="space-between">
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <BoitelContractsTable
                data={dataTable}
                loading={loadingTable}
                translation={translation}
                onTableChange={handleTableChange}
                pagination={tableChange}
                onOpenDetails={handleOpenDetailsDrawer}
                onDelete={handleDeleteContract}
              />
            </Col>
          </Row>
          <ContractDetailsDrawer
            visible={detailsDrawerVisible}
            contract={selectedContract}
            loading={detailsLoading}
            onClose={handleCloseDetailsDrawer}
            translation={translation}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          key="programacao"
          tab={translation.boitelContractsPage?.tabs?.slaughterSchedule}
        >
          <SlaughterSchedule />
        </Tabs.TabPane>
      </Tabs>
    </CardCustom>
  );
};

export default BoitelContracts;
